import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const labels = {
  disabled: "Kapalı",
  not_ready: "Hazır değil",
  ready: "Hazır",
  degraded: "Kısıtlı",
};

export default function MatrixOperationsPage() {
  const [foundation, setFoundation] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState("");
  const [reviewing, setReviewing] = useState("");
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const [foundationResult, automationResult, draftResult, exceptionResult] = await Promise.all([
        api.get("/admin/matrix-operations/dashboard"),
        api.get("/admin/matrix-automation/dashboard"),
        api.get("/admin/matrix-automation/plan-drafts", { params: { status: "guide_review" } }),
        api.get("/admin/matrix-operations/exceptions", { params: { status: "open", limit: 25 } }),
      ]);
      setFoundation(foundationResult.data);
      setAutomation(automationResult.data);
      setDrafts(draftResult.data.items || []);
      setExceptions(exceptionResult.data.items || []);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refreshVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const intervalId = window.setInterval(refreshVisible, 3000);
    window.addEventListener("focus", refreshVisible);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshVisible);
    };
  }, [load]);

  const openReview = async (draft) => {
    setReviewing(draft.id);
    try {
      const { data } = await api.get(
        `/admin/matrix-automation/plan-drafts/${encodeURIComponent(draft.id)}/review`,
      );
      setReview(data);
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setReviewing("");
    }
  };

  const publish = async (draft) => {
    if (review?.draft_id !== draft.id) return;
    setPublishing(draft.id);
    try {
      await api.post(
        `/admin/matrix-automation/plan-drafts/${encodeURIComponent(draft.id)}/approve-and-publish`,
        {
          expected_hash: draft.draft_hash,
          expected_review_hash: review.review_hash,
        },
      );
      setReview(null);
      toast.success("Program onaylandı; ilk günlük çalışma danışan panelinde açıldı.");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setPublishing("");
    }
  };

  return <div className="p-3 sm:p-5 lg:p-6 max-w-[1180px]">
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Activity size={20} />
          <h1 className="text-xl font-semibold">Operasyon Kuyruğu</h1>
        </div>
        <p className="text-[12px] text-pleiades-slate mt-1">
          Kişisel çalışma yayınlarını ve güvenli otomasyon istisnalarını tek yerden yönetin.
        </p>
      </div>
      <button onClick={load} className="h-9 px-3 border border-pleiades-border bg-white text-[12px] flex items-center gap-2">
        <RefreshCw size={14} /> Yenile
      </button>
    </div>

    {error && <div role="alert" className="mt-5 border border-red-200 bg-red-50 text-red-700 p-3 text-[12px]">{error}</div>}
    {loading && <div className="mt-6 text-[12px] text-pleiades-muted">Durum okunuyor…</div>}
    {foundation && automation && <div className="mt-3 sm:mt-5 space-y-3 sm:space-y-4">
      <details className="bg-white border border-pleiades-border">
        <summary className="cursor-pointer p-4 text-[12px] font-medium">Teknik durumu göster</summary>
      <section className="border-t border-pleiades-border p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-pleiades-muted">Genel durum</div>
            <div className="text-lg font-semibold mt-1">{labels[foundation.health.overall] || foundation.health.overall}</div>
          </div>
          {foundation.health.overall === "ready" ? <CheckCircle2 className="text-emerald-700" /> : <AlertTriangle className="text-amber-600" />}
        </div>
        {foundation.health.reasons?.length > 0 && <div className="mt-4 flex flex-wrap gap-2">
          {foundation.health.reasons.map((reason) => <span key={reason} className="px-2 py-1 bg-amber-50 border border-amber-200 text-amber-800 text-[10px]">{reason}</span>)}
        </div>}
      </section>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Kuyruk" value={foundation.summary.queue_depth} />
        <Metric label="Anket kuyruğu" value={automation.counts.questionnaires_queued} />
        <Metric label="Rehber onaylı plan" value={automation.counts.plans_guide_review} />
        <Metric label="Açık istisna" value={automation.counts.open_exceptions} />
      </section>
      </details>

      <section className="bg-white border border-pleiades-border">
        <div className="p-5 border-b border-pleiades-border">
          <h2 className="text-[14px] font-semibold">İlk yayın onayı bekleyen kişisel planlar</h2>
          <p className="text-[11px] text-pleiades-muted mt-1">Yalnız onaylı çalışma içerikleri kullanılır. Admin yayın onayından sonra ilk günlük çalışma danışan panelinde açılır.</p>
        </div>
        {drafts.length ? <div className="divide-y divide-pleiades-border">
          {drafts.map((draft) => <article key={draft.id} className="p-5">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-pleiades-blue">Danışan: {draft.client_id}</div>
              <h3 className="text-[13px] font-semibold mt-1">{draft.summary || "Kişiselleştirilmiş plan taslağı"}</h3>
              <div className="text-[10px] text-pleiades-muted mt-2">Taslak özeti kişisel anket yanıtlarını içermez · {draft.draft_hash}</div>
            </div>
            <button disabled={reviewing === draft.id} onClick={() => openReview(draft)} className="min-h-11 px-4 border border-pleiades-blue text-pleiades-blue text-[12px] disabled:opacity-40 shrink-0">{reviewing === draft.id ? "Açılıyor…" : "Planın tamamını incele"}</button>
            </div>
            {review?.draft_id === draft.id && <div className="mt-5 border-t border-pleiades-border pt-5">
              <div className="text-[11px] text-pleiades-muted">Yayınlanacak içerik · katalog {review.catalog_version} · inceleme kaydı {review.review_hash}</div>
              <div className="mt-4 space-y-4">
                {(review.blocks || []).map((block, index) => <section key={`${block.template_id}-${block.offset_days}`} className="border border-pleiades-border bg-pleiades-ivory/40 p-4">
                  <h4 className="text-[13px] font-semibold">{index + 1}. {block.title}</h4>
                  <div className="text-[10px] text-pleiades-muted mt-1">Gün {Number(block.offset_days) + 1} · Şablon {block.template_id}</div>
                  <div className="mt-3 text-[12px] leading-[1.7] text-pleiades-slate whitespace-pre-wrap">{block.body}</div>
                </section>)}
              </div>
              <button disabled={publishing === draft.id} onClick={() => publish(draft)} className="min-h-11 px-4 mt-4 bg-pleiades-blue text-white text-[12px] disabled:opacity-40">{publishing === draft.id ? "Yayınlanıyor…" : "İçeriğin tamamını kontrol ettim, yayınla"}</button>
            </div>}
          </article>)}
        </div> : <div className="p-6 text-[12px] text-pleiades-muted">Onay bekleyen kişisel plan yok.</div>}
      </section>

      <section className="bg-white border border-pleiades-border">
        <div className="p-5 border-b border-pleiades-border">
          <h2 className="text-[14px] font-semibold">Açık istisnalar</h2>
          <p className="text-[11px] text-pleiades-muted mt-1">Katalog/hash veya güvenli otomasyon sınırına takılan işlemler.</p>
        </div>
        {exceptions.length ? <div className="divide-y divide-pleiades-border">
          {exceptions.map((item) => <div key={item.id} className="p-4 flex items-center gap-4 text-[12px]">
            <AlertTriangle size={15} className="text-amber-700 shrink-0" />
            <div className="min-w-0"><div className="font-medium">{item.reason_code}</div><div className="text-[10px] text-pleiades-muted mt-1">{item.aggregate_type} · {item.aggregate_id}</div></div>
            <span className="ml-auto text-[10px] uppercase text-pleiades-muted">{item.severity}</span>
          </div>)}
        </div> : <div className="p-6 text-[12px] text-pleiades-muted">Açık istisna yok.</div>}
      </section>

      <section className="bg-white border border-pleiades-border p-5">
        <h2 className="text-[13px] font-semibold">Bağımlılıklar</h2>
        <div className="grid sm:grid-cols-2 gap-2 mt-3 text-[12px]">
          <Status label="Veritabanı" ok={foundation.health.dependencies.database} />
          <Status label="GridFS metadata" ok={foundation.health.dependencies.gridfs_metadata_read} />
          <Status label="İndeksler" ok={foundation.health.indexes_ready} />
          <Status label="Worker heartbeat" ok={Boolean(foundation.summary.worker_heartbeat_at)} />
          <Status label="AI sağlayıcı" ok={automation.readiness.ai} />
        </div>
      </section>
    </div>}
  </div>;
}

function Metric({ label, value }) {
  return <div className="bg-white border border-pleiades-border p-4"><div className="text-[10px] uppercase tracking-wider text-pleiades-muted">{label}</div><div className="text-xl font-semibold mt-2">{value ?? "—"}</div></div>;
}

function Status({ label, ok }) {
  return <div className="flex items-center justify-between border border-pleiades-border px-3 py-2"><span>{label}</span><span className={ok ? "text-emerald-700" : "text-amber-700"}>{ok ? "Hazır" : "Hazır değil"}</span></div>;
}
